import type { OrderDateField } from "@threadheads/shared";

import { toast } from "@/hooks/use-toast";
import { trpc } from "@/lib/trpc";

// PR #1989 review: the history rarely changes under the operator (only this
// panel and the partner portal write it), so don't refetch it on every focus.
const HISTORY_STALE_TIME_MS = 60_000;

/**
 * The Ready ETA / Arrival ETA revision history for one order.
 *
 * PR #1989 review: the ONE definition of this query (input + staleTime). The
 * detail page calls it with the route id so the fetch starts alongside
 * getOrder (httpBatchLink sends both in one request), and the dates panel
 * calls it again through useOrderDates; the identical `{ orderId }` input
 * means the panel reads the page's cache entry instead of starting a second
 * fetch after the order loads, so the "Revised..." line does not pop in late.
 */
export function useOrderDateHistory(orderId: string | undefined, enabled: boolean) {
  return trpc.purchaseOrders.getOrderDateHistory.useQuery(
    { orderId: orderId ?? "" },
    { enabled: enabled && !!orderId, staleTime: HISTORY_STALE_TIME_MS }
  );
}

/**
 * Editable PO dates: the update mutation plus the Ready ETA and Arrival ETA
 * revision histories.
 * Both purchase orders and transfers go through the purchaseOrders router.
 */
export function useOrderDates(params: { orderId: string; locationId: string }) {
  const { orderId, locationId } = params;
  const utils = trpc.useUtils();

  const history = useOrderDateHistory(orderId, true);

  const mutation = trpc.purchaseOrders.updateOrderDates.useMutation({
    // PR #1989 review: return the refetch so mutateAsync resolves on fresh
    // data (TanStack v5 awaits onSuccess); the row then closes onto the new
    // value instead of flashing the old one. The saved row is the success cue
    // (DESIGN.md §15.4), so no toast. The list refetch is not awaited.
    onSuccess: (_data, vars) => {
      void utils.purchaseOrders.getOrders.invalidate();
      const etaChanged = vars.etaDate !== undefined || vars.arrivalEtaDate !== undefined;
      return Promise.all([
        utils.purchaseOrders.getOrder.invalidate({ orderId }),
        ...(etaChanged ? [utils.purchaseOrders.getOrderDateHistory.invalidate({ orderId })] : []),
      ]);
    },
    onError: (err) => {
      toast({ title: "Failed to update date", description: err.message, variant: "destructive" });
    },
  });

  const saveDate = (field: OrderDateField, value: string | null) =>
    mutation.mutateAsync({ orderId, locationId, [field]: value });

  // PR #1989 review: no `isSaving`; each row tracks its own in-flight save.
  return {
    saveDate,
    etaHistory: history.data?.eta ?? [],
    arrivalEtaHistory: history.data?.arrivalEta ?? [],
  };
}
